/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import { setStoplightFilter } from '../../../redux/actions';
import { logReportsClick } from '../../../utils/analyticsHelpers';
/**
 * Global Type Definition Imports
 */
import {
  Filter,
  MetricsData,
  StoplightApiMetrics
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface EmpStoplightMetricChartProps {
  employeeSummaryReportFilter: Filter;
  stoplightMetrics: StoplightApiMetrics;
  isFacilityList: boolean;
}

const EmpStoplightMetricChart: FC<EmpStoplightMetricChartProps> = ({
  employeeSummaryReportFilter,
  stoplightMetrics,
  isFacilityList
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: stoplightMetrics.totalRecordsCount,
      metrics: [
        {
          label: 'In Progress',
          value: stoplightMetrics.openedRecordsCount,
          color: '#d7a93d'
        },
        {
          label: 'Completed',
          value: stoplightMetrics.completedRecordsCount,
          color: '#467d20'
        },
        {
          label: 'Deferred',
          value: stoplightMetrics.deferredRecordsCount,
          color: '#cc3435'
        }
      ]
    };
  }, [stoplightMetrics]);

  const openStoplightTable = () => {
    dispatch(
      setStoplightFilter({
        ...employeeSummaryReportFilter,
        startDate:
          employeeSummaryReportFilter.startDate ??
          format(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            'yyyy-MM-dd'
          ),
        category: '',
        subCategory: '',
        createdBy: '',
        hasAttachedIssues: false,
        hasUpdate: false
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'stoplight'));
    setTimeout(() => {
      navigateTo('/employees/stoplight', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer
      onClick={() => {
        if (isFacilityList) {
          openStoplightTable();
        } else {
          window.open('https://resolve.hcahealthcare.cloud/', '_blank');
        }
      }}
    >
      <HeaderContainer style={{ paddingLeft: '3.5em' }}>
        <NeuIcon size="1x" color="green" className="ms-1">
          traffic
        </NeuIcon>
        <TitleContainer style={{ marginLeft: '0.5rem' }}>
          {isFacilityList && stoplightMetrics.totalRecordsCount} Stoplights
        </TitleContainer>
      </HeaderContainer>
      {isFacilityList ? (
        metricsData.metrics.length > 0 ? (
          <TransitionalBarChart metricsData={metricsData} />
        ) : (
          ''
        )
      ) : (
        <div
          style={{
            width: '300px',
            height: '225px',
            padding: '1rem',
            fontWeight: 500
          }}
        >
          Stoplight summaries are only available at the facility level. To see
          it at a more granular level, please click this widget to head over to
          Resolve.
        </div>
      )}
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    stoplightMetrics: state.StoplightReducer.stoplightMetrics
  };
};
export default connect(mapReduxStateToProps)(EmpStoplightMetricChart);
